/**
 * Camada 1 de 3 — PRIMITIVAS.
 *
 * Este é o ÚNICO arquivo do repositório onde um literal hexadecimal pode
 * nascer. Camada 2 (`cores.semanticas.ts`) dá papel a estes valores; a
 * camada 3 (`tema/criarTema.ts`) os entrega ao MUI. Componente de feature
 * nunca lê este arquivo — lê semântico.
 *
 * O teste `apps/web/src/tema/contraste.test.ts` varre o código atrás de
 * hexadecimal fora daqui e falha o build. Não é convenção, é mecanismo.
 *
 * Todo número de contraste citado abaixo foi medido pela fórmula WCAG 2.1
 * (relative luminance), não estimado.
 */

/** Paleta de marca da Anery, como veio da identidade visual. */
export const marca = {
  /** Primária. 12.86:1 sobre branco — livre para texto, ícone e preenchimento. */
  primaria: '#0B2D72',
  /** Acento. 6.44:1 sobre branco — passa AA para texto. */
  acento: '#6950A1',
  /** Texto de marca. 7.00:1 sobre branco. */
  texto: '#595959',
  /**
   * Secundária. 2.56:1 sobre branco — REPROVA para texto.
   * É cor de SUPERFÍCIE, nunca de tinta. Use `teal.*` abaixo.
   */
  secundaria: '#00B5AD',
  /**
   * Apoio. 1.65:1 sobre branco — REPROVA para texto.
   * É cor de SUPERFÍCIE, nunca de tinta. Use `pessego.*` abaixo.
   */
  apoio: '#FFBC7D',
  /** Rosa. 4.96:1 sobre branco. Medida no header de anery.com.br. */
  rosa: '#CC3366',
} as const;

/**
 * Derivadas do teal. A secundária da marca só é legítima como preenchimento;
 * cada outro papel tem um tom próprio, já medido.
 */
export const teal = {
  /** Preenchimento de selo/superfície. Nunca recebe texto que não seja `tinta`. */
  preenchimento: marca.secundaria,
  /** Tinta sobre o preenchimento. 5.72:1 sobre #00B5AD. */
  tinta: '#062E2C',
  /** Borda e ícone. 3.47:1 sobre branco, 3.29:1 sobre a página. */
  borda: '#009A93',
  /** Texto sobre superfície clara. 5.71:1 sobre branco, 5.41:1 sobre a página. */
  texto: '#00736E',
  /** Preenchimento claro de chip. */
  claro: '#E1F4F3',
} as const;

/**
 * Derivadas do pêssego. Mesma lógica do teal: a cor de apoio só preenche.
 */
export const pessego = {
  preenchimento: marca.apoio,
  /** 7.48:1 sobre #FFBC7D. */
  tinta: '#4A2F0D',
  /** 3.92:1 sobre branco, 3.72:1 sobre a página. */
  borda: '#C26A00',
  /** 6.80:1 sobre branco, 6.45:1 sobre a página. */
  texto: '#8A4B00',
  claro: '#FFF0DE',
} as const;

/**
 * Vermelho e verde NÃO existem na marca. São derivados aqui de propósito,
 * para o teal não acumular dois significados ("secundária" e "deu certo").
 */
export const erro = {
  /** 6.54:1 sobre branco, 6.20:1 sobre a página. */
  base: '#B3261E',
  /** 7.51:1 sobre o claro, 9.12:1 sobre branco. */
  tinta: '#8C1D17',
  claro: '#FBE4E2',
} as const;

/**
 * Derivadas do acento. Usadas pela fila de DLQ — que precisa de um tom
 * distinto de erro: "falhou agora" e "desistiu depois de N tentativas" são
 * situações diferentes e não podem dividir a mesma cor.
 */
export const acento = {
  base: marca.acento,
  /** 8.34:1 sobre o claro, 10.03:1 sobre branco. */
  tinta: '#4A3775',
  claro: '#EDE8F5',
} as const;

export const sucesso = {
  /** 5.02:1 sobre branco, 4.76:1 sobre a página. O mais apertado dos dois. */
  base: '#1B7F4B',
  /** 6.57:1 sobre o claro, 7.61:1 sobre branco. */
  tinta: '#12603A',
  claro: '#E3F2E9',
} as const;

/** Derivadas do rosa. O estado "barrado" não é `erro`: erro é falha nossa. */
export const rosa = {
  base: marca.rosa,
  /** 5.58:1 sobre o claro, 6.44:1 sobre branco. */
  tinta: '#AD2B57',
  claro: '#FAEBF0',
} as const;

/**
 * Marca-texto. Não é estado (`pessego` é "atenção"): é o amarelo de quem grifa
 * um trecho para mostrar de onde um valor saiu. A tinta continua a do texto.
 */
export const realce = {
  /** `neutro.tintaForte` sobre ele: 9.88:1. */
  marcaTexto: '#FFE08A',
} as const;

/**
 * Rampa sequencial da primária, para gráfico.
 *
 * Uma rampa e não um conjunto categórico: os dois gráficos do dashboard
 * medem MAGNITUDE (quantas pendências, que fatia da glosa), não identidade.
 * Rosa e pêssego seriam as cores óbvias para completar quatro categorias, e
 * é exatamente o que não se pode fazer: neste produto elas significam
 * `barrado` e `atenção`, e reaproveitá-las como "série 3" faria a cor mentir.
 *
 * Os quatro degraus são navy misturado ao branco, escolhidos pelo contraste
 * e não pela proporção da mistura — todos ≥ 3:1 sobre `superficie`, que é o
 * mínimo para elemento gráfico que carrega informação (WCAG 1.4.11), e
 * monotônicos em luminosidade, que é o que faz uma rampa sequencial ser
 * legível sem legenda de cor.
 */
export const rampaPrimaria = {
  /** 3.10:1 sobre branco. */
  passo1: '#8193B6',
  /** 4.60:1 sobre branco. */
  passo2: '#5F75A2',
  /** 7.20:1 sobre branco. */
  passo3: '#3B568E',
  /** 12.80:1 sobre branco. É a própria primária. */
  passo4: marca.primaria,
} as const;

/**
 * Neutros. O fundo de página é levemente azulado de propósito: branco puro
 * em jornada de oito horas ofusca. Cartão e linha de tabela ficam brancos,
 * e é o contraste entre os dois que separa a superfície do fundo.
 */
export const neutro = {
  /** Texto de leitura longa. 12.75:1 sobre branco. */
  tintaForte: '#2E3338',
  /** Texto secundário. Igual a `marca.texto`, nomeado pelo papel. */
  tintaMedia: marca.texto,
  /** Texto desabilitado e borda forte. 5.00:1 sobre branco, 4.74:1 sobre a página. */
  tintaFraca: '#6A7078',
  /** Tinta sobre chip neutro. 7.23:1 sobre #EDF0F4. */
  tintaDeChip: '#4A4F55',
  /** Preenchimento de chip neutro. Recebe `tintaDeChip` (7.23:1). */
  chip: '#EDF0F4',
  /**
   * Cabeçalho de tabela e barras. Mais claro que `chip` de propósito:
   * em #EDF0F4 o verde de sucesso cai para 4.39:1 e reprova. Aqui o pior
   * par mede 4.62:1. Clarear é barato; descobrir isso em produção não é.
   */
  superficieSutil: '#F4F6FA',
  /** Separador. Decorativo — não carrega informação, por isso sem mínimo. */
  separador: '#E4E8EE',
  /** Fundo da página. */
  fundoDaPagina: '#F7F9FC',
  /** Cartão, linha de tabela, papel. */
  superficie: '#FFFFFF',
  /** Tinta sobre superfície escura (primária, erro, sucesso, acento). */
  tintaInversa: '#FFFFFF',
} as const;
